import { z } from 'zod';

/**
 * Typed links between an agent and external work items. This generalizes the
 * legacy single `Agent.prUrl` field: an agent can be associated with any
 * number of work items, each identified by a `kind` plus a kind-scoped
 * canonical id ({@link AgentAssociationSchema.externalId}).
 *
 * Kinds are an open, registered set: the Reflex core provides
 * {@link ASSOCIATION_KIND_PULL_REQUEST}; plugins declare their own via
 * `PluginDefinition.associationKinds` (e.g. `linear_issue`, `jira_issue`) and
 * the server rejects writes for unregistered kinds. Each provider owns the
 * shape of its {@link AgentAssociationSchema.data} payload and registers the
 * web chip that displays its kind — the core model deliberately knows nothing
 * beyond (kind, id, role, data).
 */

/** The association kind the Reflex core itself provides. */
export const ASSOCIATION_KIND_PULL_REQUEST = 'pull_request';

/**
 * The other two core kinds, both taxonomy rather than external work:
 * membership in a user-made agent group, and an applied label describing the
 * kind of work the run is. They live here rather than in their own modules so
 * every core kind — and the display priority that orders them — sits in one
 * place; those modules build on these constants.
 */
export const ASSOCIATION_KIND_AGENT_GROUP = 'agent_group';
export const ASSOCIATION_KIND_AGENT_LABEL = 'agent_label';

/**
 * The agent's relationship to the associated item.
 * - `creator`: the agent created the item (opened the pull request).
 * - `reviewer`: the agent reviewed an item it did not create.
 * - `assignee`: the agent was launched to work the item (an issue).
 * - `member`: the agent belongs to the item — the role agent-group
 *   membership uses, where the "item" is a user-made group rather than
 *   external work.
 * - `mentioned`: a person brought the item into the agent's thread (pasted
 *   a PR or issue), or the agent attached it itself. The weakest link: the
 *   agent neither made nor was assigned the item, it just knows about it.
 */
export const AgentAssociationRoleSchema = z.enum([
  'creator',
  'reviewer',
  'assignee',
  'member',
  'mentioned',
]);
export type AgentAssociationRole = z.infer<typeof AgentAssociationRoleSchema>;

/** Roles that make sense for a `pull_request` association. */
export const AgentPrAssociationRoleSchema = z.enum(['creator', 'reviewer']);
export type AgentPrAssociationRole = z.infer<typeof AgentPrAssociationRoleSchema>;

/**
 * Fold a free-form wire value into a PR association role. Anything other than
 * an explicit `reviewer` means `creator`: older on-box CLIs send no role, and
 * that path only ever registered PRs the agent opened itself. This is the one
 * place that invariant lives — every boundary (bridge notify, runner event
 * handler, legacy-payload rendering) narrows through here.
 */
export function narrowPrAssociationRole(value: unknown): AgentPrAssociationRole {
  return value === 'reviewer' ? 'reviewer' : 'creator';
}

export const AgentAssociationSchema = z
  .object({
    id: z.string(),
    agentId: z.string(),
    /**
     * Registered association kind (e.g. `pull_request`, `linear_issue`).
     * Open by design — plugins contribute kinds — so consumers must treat
     * unknown values as displayable-but-opaque, never as an error.
     */
    kind: z.string(),
    role: AgentAssociationRoleSchema,
    /**
     * Canonical id of the item within its kind. The id is chosen so the item
     * is collectable from it plus org context alone: `owner/repo#42` for a
     * pull request (see {@link prExternalId}), the issue key (`ENG-123`) for
     * a tracker issue. Uniqueness is per (agent, kind, role, externalId).
     */
    externalId: z.string(),
    /**
     * Kind-specific payload owned by the kind's provider (e.g. `url`,
     * `title`, `branch` for PRs). The core stores it opaquely; display
     * components registered for the kind know how to read it.
     */
    data: z.record(z.string(), z.unknown()).nullable().optional(),
    createdAt: z.number(),
  })
  .meta({ id: 'AgentAssociation' });
export type AgentAssociation = z.infer<typeof AgentAssociationSchema>;

/**
 * Input for recording an association — everything the stored row carries
 * except the server-minted `id`/`createdAt`, plus the owning organization.
 * Shared so the server service and the plugin-facing
 * `IntegrationAgentRunner.recordAssociation` contract stay one type.
 */
export const RecordAgentAssociationInputSchema = AgentAssociationSchema.omit({
  id: true,
  createdAt: true,
}).extend({
  /**
   * Owning tenant, copied from the agent row. Required: every association
   * hangs off an agent, `agents.organization_id` is `NOT NULL` at the
   * database level, and a NULL-org association is read through the
   * `'shared'` org-scope mode — so minting one surfaces the chip in every
   * tenant.
   */
  organizationId: z.string().min(1),
});
export type RecordAgentAssociationInput = z.infer<typeof RecordAgentAssociationInputSchema>;

/**
 * Query for the org-scoped association listing (the workspace "pull requests
 * for this project" view): narrow by kind and/or a kind-scoped external-id
 * prefix — for pull requests, the project's `owner/repo#` prefix selects
 * every PR of that repo.
 */
export const ListAgentAssociationsQuerySchema = z.object({
  kind: z.string().optional(),
  /** Convenience for `kind=pull_request`: `owner/repo` becomes the `owner/repo#` id prefix. */
  repoSlug: z.string().optional(),
});
export type ListAgentAssociationsQuery = z.infer<typeof ListAgentAssociationsQuerySchema>;

/** Just enough of the agent to render "which agent did this" next to an item. */
export const AgentAssociationAgentSummarySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    agentType: z.string(),
    status: z.string(),
    userId: z.string().nullable().optional(),
    createdAt: z.number(),
  })
  .meta({ id: 'AgentAssociationAgentSummary' });
export type AgentAssociationAgentSummary = z.infer<typeof AgentAssociationAgentSummarySchema>;

export const AgentAssociationWithAgentSchema = AgentAssociationSchema.extend({
  agent: AgentAssociationAgentSummarySchema,
}).meta({ id: 'AgentAssociationWithAgent' });
export type AgentAssociationWithAgent = z.infer<typeof AgentAssociationWithAgentSchema>;

export const ListAgentAssociationsResponseSchema = z
  .object({
    associations: z.array(AgentAssociationWithAgentSchema),
  })
  .meta({ id: 'ListAgentAssociationsResponse' });
export type ListAgentAssociationsResponse = z.infer<typeof ListAgentAssociationsResponseSchema>;

// ── Pull-request kind: canonical id helpers ──────────────────────────
//
// The PR external id is `owner/repo#<number>` — the one id GitHub webhooks,
// the on-box CLI, and the web UI can all reconstruct independently, and from
// which the html URL is derivable without storing it.

/** Build the canonical `owner/repo#42` external id for a pull request. */
export function prExternalId(repoSlug: string, number: number): string {
  return `${repoSlug}#${number}`;
}

/**
 * Parse an `owner/repo#42` PR external id back into its parts. Owner and
 * repo segments exclude `/` (as on GitHub), which also keeps the pattern
 * free of the backtracking ambiguity an all-inclusive segment class has.
 */
export function parsePrExternalId(externalId: string): { repoSlug: string; number: number } | null {
  const m = /^([^\s#/]+\/[^\s#/]+)#(\d+)$/.exec(externalId);
  return m ? { repoSlug: m[1], number: Number(m[2]) } : null;
}

/** Derive the GitHub html URL from a PR external id, when it parses. */
export function prUrlFromExternalId(externalId: string): string | null {
  const parsed = parsePrExternalId(externalId);
  return parsed ? `https://github.com/${parsed.repoSlug}/pull/${parsed.number}` : null;
}

/**
 * The display shape of an association: the stored fields minus row identity,
 * plus the provider-owned `data` payload. Chip renderers registered for a
 * kind receive this.
 */
export const AgentAssociationRefSchema = AgentAssociationSchema.pick({
  kind: true,
  role: true,
  externalId: true,
  data: true,
});
export type AgentAssociationRef = z.infer<typeof AgentAssociationRefSchema>;

/** Read a string field out of an association's kind-specific data payload. */
export function associationDataString(
  ref: Pick<AgentAssociationRef, 'data'>,
  key: string,
): string | null {
  const value = ref.data?.[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * The link target of an association: the provider-supplied `data.url`,
 * falling back to the URL derivable from the external id (pull requests).
 */
export function associationUrl(ref: AgentAssociationRef): string | null {
  const direct = associationDataString(ref, 'url');
  if (direct) return direct;
  if (ref.kind === ASSOCIATION_KIND_PULL_REQUEST) return prUrlFromExternalId(ref.externalId);
  return null;
}

/**
 * Lifecycle state of a `pull_request` association, persisted into `data.state`
 * by the registering CLI and kept current by the `pull_request` webhook. See
 * {@link prAssociationState}.
 * - `open`   — the PR exists, is ready for review, and is neither merged nor closed;
 * - `draft`  — the PR is open but still marked draft on GitHub;
 * - `merged` — the PR was merged (the agent's work landed);
 * - `closed` — the PR was closed without merging.
 *
 * `draft` is a stored state, not a display-only refinement: agents are told to
 * open draft PRs, so it is the *usual* state of a freshly registered PR. Left
 * unstored, every agent PR chip would render `open` until a live GitHub fetch
 * corrected it — a badge that is wrong on arrival and changes under the user.
 */
export const PrAssociationStateSchema = z.enum(['open', 'draft', 'merged', 'closed']);
export type PrAssociationState = z.infer<typeof PrAssociationStateSchema>;

/** States a PR can still move on from, so a chip showing one re-verifies it. */
const LIVE_PR_STATES = new Set<PrAssociationState>(['open', 'draft']);

/**
 * Whether a stored PR state can still change (`open` / `draft`) as opposed to
 * being terminal (`merged` / `closed`). The one place the "is this worth
 * re-checking against GitHub" question is answered, so the chip's eager-verify
 * gate and the batch's scope selection can't drift apart.
 */
export function isLivePrState(state: PrAssociationState): boolean {
  return LIVE_PR_STATES.has(state);
}

/** The `data` key the PR lifecycle state is stored under. */
export const PR_ASSOCIATION_STATE_KEY = 'state';

/**
 * The lifecycle state of a pull-request association, read from the
 * provider-written `data.state` and defaulting to `open`: an association
 * written before this field existed (or by a client that omits it) is
 * overwhelmingly an open PR. Unknown/malformed values also read as `open`.
 */
export function prAssociationState(ref: Pick<AgentAssociationRef, 'data'>): PrAssociationState {
  const parsed = PrAssociationStateSchema.safeParse(ref.data?.[PR_ASSOCIATION_STATE_KEY]);
  return parsed.success ? parsed.data : 'open';
}

/**
 * The `data` key holding the version of the stored PR state: GitHub's
 * `updated_at` for the snapshot the state was read from, in epoch ms. Written
 * only by the webhook sync; absent on a state recorded at registration.
 */
export const PR_ASSOCIATION_STATE_UPDATED_AT_KEY = 'stateUpdatedAt';

/**
 * A PR state plus the GitHub `updated_at` (epoch ms) of the snapshot it came
 * from. `updatedAt` is `null` when the source carries no version — a state the
 * CLI recorded at registration, or a push from an older server.
 */
export const PrStateSnapshotSchema = z.object({
  state: PrAssociationStateSchema,
  updatedAt: z.number().nullable(),
});
export type PrStateSnapshot = z.infer<typeof PrStateSnapshotSchema>;

/** The stored state of a `pull_request` association together with its version. */
export function prAssociationSnapshot(ref: Pick<AgentAssociationRef, 'data'>): PrStateSnapshot {
  const version = PrStateSnapshotSchema.shape.updatedAt.safeParse(
    ref.data?.[PR_ASSOCIATION_STATE_UPDATED_AT_KEY] ?? null,
  );
  return {
    state: prAssociationState(ref),
    updatedAt: version.success ? version.data : null,
  };
}

/** GitHub's ISO `updated_at` as epoch ms, or `null` when absent or unparsable. */
export function parseGitHubTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Whether `incoming` should replace `current` as the PR's state. Webhooks
 * arrive out of order and are drained on several replicas, and a live fetch
 * can race a push, so "last write wins" lets an older snapshot overwrite a
 * newer one — a merged PR flipping back to `open`. The rule:
 *
 * - `merged` is final on GitHub (a merged PR can't be reopened), so nothing
 *   else replaces it;
 * - otherwise the snapshot with the newer `updated_at` wins, ties going to
 *   `incoming` (GitHub's timestamps have one-second resolution);
 * - an unversioned snapshot only replaces another unversioned one.
 *
 * The server's guarded `setPullRequestState` write applies the same rule in
 * SQL; keep the two in step.
 */
export function isNewerPrSnapshot(
  incoming: PrStateSnapshot,
  current: PrStateSnapshot | null | undefined,
): boolean {
  if (!current) return true;
  if (current.state === 'merged' && incoming.state !== 'merged') return false;
  if (incoming.updatedAt === null) return current.updatedAt === null;
  if (current.updatedAt === null) return true;
  return incoming.updatedAt >= current.updatedAt;
}

/** The stored state a live GitHub PR row maps to. Merged wins over closed
 * (GitHub reports a merged PR as `closed` with `merged: true`), and draft only
 * refines an otherwise-open PR — the same ladder the web chip renders. */
export function prStateFromGitHub(pr: {
  state: 'open' | 'closed';
  merged: boolean;
  draft: boolean;
}): PrAssociationState {
  if (pr.merged) return 'merged';
  if (pr.state === 'closed') return 'closed';
  return pr.draft ? 'draft' : 'open';
}

/**
 * Display priority per association kind — lower renders first. Pull requests
 * are the headline artifact of a run, so they always lead; registered plugin
 * kinds follow in registration order, unknown kinds last. Taxonomy trails
 * everything: a group or a label is something hung on the run, not work the
 * run produced, so it yields its space to the real artifacts first. Labels
 * come last of all — they are derived, and there can be several per run.
 */
export function associationKindPriority(kind: string): number {
  if (kind === ASSOCIATION_KIND_PULL_REQUEST) return 0;
  if (kind === ASSOCIATION_KIND_AGENT_GROUP) return 2;
  return kind === ASSOCIATION_KIND_AGENT_LABEL ? 3 : 1;
}

/**
 * Every association of an agent in display order: pull requests first,
 * registration order otherwise (`Array.prototype.sort` is stable). Falls
 * back to synthesizing a single `creator` PR entry from the deprecated
 * `prUrl` field for payloads produced before associations existed (or
 * serializers that do not hydrate them).
 */
export function agentDisplayAssociations(agent: {
  prUrl?: string | null;
  associations?: AgentAssociation[] | null;
}): AgentAssociationRef[] {
  const refs: AgentAssociationRef[] = (agent.associations ?? [])
    .filter((a) => a.externalId.trim().length > 0)
    .map(({ kind, role, externalId, data }) => ({ kind, role, externalId, data }));
  if (refs.length === 0 && agent.prUrl) {
    const legacy = legacyPrRef(agent.prUrl);
    if (legacy) refs.push(legacy);
  }
  return refs.sort((a, b) => associationKindPriority(a.kind) - associationKindPriority(b.kind));
}

/** Synthesize a PR association ref from the deprecated `prUrl` field. */
function legacyPrRef(prUrl: string): AgentAssociationRef | null {
  const m = /github\.com\/([^/]+\/[^/]+)\/pull\/(\d+)/.exec(prUrl);
  return {
    kind: ASSOCIATION_KIND_PULL_REQUEST,
    role: 'creator',
    externalId: m ? prExternalId(m[1], Number(m[2])) : prUrl,
    data: { url: prUrl },
  };
}

/**
 * The pull-request associations of an agent (legacy `prUrl` fallback
 * included), with the repo/number parsed out of the canonical id — the shape
 * PR-matching consumers (MCP `find_agent`) work with.
 */
export function agentPullRequests(agent: {
  prUrl?: string | null;
  associations?: AgentAssociation[] | null;
}): Array<{
  externalId: string;
  role: AgentPrAssociationRole;
  url: string | null;
  repoSlug: string | null;
  number: number | null;
}> {
  return agentDisplayAssociations(agent)
    .filter((ref) => ref.kind === ASSOCIATION_KIND_PULL_REQUEST)
    .map((ref) => {
      const parsed = parsePrExternalId(ref.externalId);
      return {
        externalId: ref.externalId,
        role: narrowPrAssociationRole(ref.role),
        url: associationUrl(ref),
        repoSlug: parsed?.repoSlug ?? null,
        number: parsed?.number ?? null,
      };
    });
}
